import dayjs from "dayjs";
import { call, put, putResolve, select } from "redux-saga/effects";

import {
  AvailabilityRequest,
  AvailabilityRequestEnum,
  AvailabilityResponse,
  ICategorizedResponse,
  IIdLodgings,
  IResult,
  ITrackingProperties,
  LC_ENTRY,
  LocationDescriptorEnum,
  Lodging,
  LodgingCollectionEnum,
  LodgingSelectionEnum,
  PC_ENTRY,
  PlatformEnum,
  PremierCollectionEntryProperties,
} from "redmond";

import { trackEngagementEvent, getIsTravelApp } from "@capone/common";
import { trackEvent } from "../../../api/v0/analytics/trackEvent";
import { fetchPremierCollectionAvailability as fetchAvailability } from "../../../api/v0/availability/fetchPremierCollectionAvailability";
import { fetchPCLocationAutocomplete } from "../../../api/v1/autocomplete/fetchPCLocationAutocomplete";
import { IStoreState } from "../../../reducers/types";
import Logger from "../../../utils/logger";
import { PATH_AVAILABILITY, PATH_HOME } from "../../../utils/paths";
import { isMobile } from "../../../utils/userAgent";
import { actions as bookActions } from "../../book/actions";
import { actions as searchActions } from "../../search/actions";
import {
  getAdultsCount,
  getChildren,
  getFromDate,
  getLocation,
  getPremierCollectionEntryProperties,
  getUntilDate,
} from "../../search/reducer";
import { actionTypes, actions } from "../actions";
import { getPremierCollectionAvailabilityNextPageToken } from "../reducer";

import { getRewardsAccountsReferenceIds } from "../../rewards/reducer";
import { findMatchingLocation } from "../utils/findMatchingLocation";
import {
  IPremierCollectionAvailabilityParsedQuery,
  parseQueryString,
} from "../utils/parseQueryString";

export function* fetchPremierCollectionAvailabilitySaga(
  action: actions.IFetchPremierCollectionAvailability
) {
  try {
    let requestBody: AvailabilityRequest;
    const state: IStoreState = yield select();
    yield put(bookActions.redoSearch());

    switch (action.type) {
      case actionTypes.FETCH_INITIAL_PREMIER_COLLECTION_AVAILABILITY: {
        const requestData: {
          fromDate: Date;
          untilDate: Date;
          location: IResult;
          adultsCount: number;
          children: number[];
        } = yield call(
          getPremierCollectionAvailabilityRequestParameters,
          action
        );
        const {
          fromDate,
          untilDate,
          location,
          adultsCount: adults,
          children,
        } = requestData;

        // If we received empty request data, we are redirecting and should not make the request.
        if (JSON.stringify(requestData) === "{}") return;
        if (action.mapBounds != null) {
          yield put(searchActions.setLocation(null));
        }

        requestBody = {
          lodgingSelection:
            action.mapBounds != null
              ? {
                  LodgingSelection: LodgingSelectionEnum.Location,
                  descriptor: {
                    LocationDescriptor: LocationDescriptorEnum.BoundingBox,
                    ...action.mapBounds,
                  },
                }
              : (location?.id as IIdLodgings).lodgingSelection,
          stayDates: {
            from: dayjs(fromDate).format("YYYY-MM-DD"),
            until: dayjs(untilDate).format("YYYY-MM-DD"),
          },
          guests: { adults, children },
          platform: getIsTravelApp().isTravelApp
            ? PlatformEnum.App
            : isMobile()
            ? PlatformEnum.Mobile
            : PlatformEnum.Desktop,
          progressiveConfig: {},
          excludeNonLuxuryLodgings: true,
          lodgingCollection: action.lodgingCollection,
          AvailabilityRequest: AvailabilityRequestEnum.InitialSearch,
          rooms: { numberOfRooms: 1 },
        };

        break;
      }
      case actionTypes.FETCH_MORE_PREMIER_COLLECTION_AVAILABILITY: {
        const nextPageToken =
          getPremierCollectionAvailabilityNextPageToken(state);

        if (!nextPageToken) return;

        requestBody = {
          moreToken: nextPageToken,
          progressiveConfig: {},
          excludeNonLuxuryLodgings: true,
          lodgingCollection: action.lodgingCollection,
          AvailabilityRequest: AvailabilityRequestEnum.FollowUpSearch,
        };

        break;
      }
    }

    const availabilityResponse: AvailabilityResponse = yield fetchAvailability(
      requestBody
    );

    yield put(
      actions.setPremierCollectionAvailabilityResults({
        payload: availabilityResponse,
      })
    );

    const properties: ITrackingProperties<PremierCollectionEntryProperties> =
      yield select(getPremierCollectionEntryProperties);

    trackEvent({
      eventName:
        action.lodgingCollection === LodgingCollectionEnum.Lifestyle
          ? LC_ENTRY
          : PC_ENTRY,
      properties: {
        ...properties.properties,
        number_of_properties: availabilityResponse.lodgings.length,
      },
      encryptedProperties: properties.encryptedProperties,
    });
    if (
      action.type === actionTypes.FETCH_INITIAL_PREMIER_COLLECTION_AVAILABILITY
    ) {
      const requestData: {
        fromDate: Date;
        untilDate: Date;
        location: IResult;
        adultsCount: number;
        children: number[];
      } = yield call(getPremierCollectionAvailabilityRequestParameters, action);
      const { fromDate, untilDate, location } = requestData;

      const accountReferenceIds: string[] =
        getRewardsAccountsReferenceIds(state);

      const initialLowestPricedLodging = availabilityResponse.lodgings.reduce(
        (prev, curr) => {
          const available =
            typeof curr.available === "undefined" ? true : curr.available;
          if (available && curr.price) {
            if (
              prev.price &&
              curr.price.nightlyPrice.fiat.value >
                prev?.price.nightlyPrice.fiat.value
            ) {
              if (
                curr.price.nightlyPrice.fiat.value >
                prev?.price.nightlyPrice.fiat.value
              ) {
                return prev;
                // eslint-disable-next-line no-else-return
              } else {
                return curr;
              }
              // eslint-disable-next-line no-else-return
            } else {
              return curr;
            }
          }
          return prev;
        },
        {} as Lodging
      );

      trackEngagementEvent({
        event: {
          event_type: "hotel_search",
          lowest_hotel_price:
            initialLowestPricedLodging.price?.nightlyPrice.fiat.value ??
            undefined,
          search_term: location.label,
          check_in_date_timestamp: dayjs(fromDate).valueOf(),
          check_out_date_timestamp: dayjs(untilDate).valueOf(),
          account_reference_ids: accountReferenceIds,
          lodging_type: action.lodgingCollection,
        },
      });
    }
  } catch (e) {
    yield put(actions.setPremierCollectionAvailabilityCallStateFailed());
    Logger.debug(e);
  }
}

const getExistingStateVariables = (state: IStoreState) => ({
  fromDate: getFromDate(state),
  untilDate: getUntilDate(state),
  location: getLocation(state),
  adultsCount: getAdultsCount(state),
  children: getChildren(state),
});

const shouldRedirect = ({ fromDate, untilDate, location }: any) =>
  !fromDate || !untilDate || !location;

function* getPremierCollectionAvailabilityRequestParameters(
  fetchPremierCollectionAvailability: actions.IFetchPremierCollectionAvailability
) {
  const state: IStoreState = yield select();
  const { history } = fetchPremierCollectionAvailability;

  let { fromDate, untilDate, location, adultsCount, children } =
    getExistingStateVariables(state);

  const parsedQueryString = parseQueryString(history);

  if (
    parsedQueryString != null &&
    (!fromDate ||
      !untilDate ||
      !location ||
      !adultsCount ||
      (location?.id as IIdLodgings).lodgingSelection.searchTerm !==
        parsedQueryString.locationName)
  ) {
    const { correspondingLocation } = yield fetchLocation(parsedQueryString);

    [fromDate, untilDate, location, adultsCount, children] = [
      parsedQueryString.fromDate,
      parsedQueryString.untilDate,
      correspondingLocation,
      parsedQueryString.adultsCount || 2,
      parsedQueryString.childrenCount > 0
        ? new Array(parsedQueryString.childrenCount).fill(17)
        : [],
    ];

    // If we know we are missing the data we should redirect the user home rather than make a bad request.
    // TODO: this should probably be monitored in the future. If we get a lot of these something is likely wrong.
    if (
      shouldRedirect({
        fromDate,
        untilDate,
        location,
        adultsCount,
      })
    ) {
      if (
        fetchPremierCollectionAvailability.history.location.pathname ===
        PATH_AVAILABILITY
      )
        fetchPremierCollectionAvailability.history.push(PATH_HOME);
      return {};
    }

    yield putResolve(searchActions.setFromDate(fromDate));
    yield putResolve(searchActions.setUntilDate(untilDate));
    yield putResolve(searchActions.setLocation(location));
    yield putResolve(
      searchActions.setOccupancyCounts({
        adults: adultsCount,
        children,
      })
    );
  }

  yield put(actions.setSearchedDates(fromDate!, untilDate!));
  yield put(actions.setSearchedLocationResult(location));
  yield put(
    actions.setSearchedOccupancyCounts({ adults: adultsCount, children })
  );

  return {
    fromDate,
    untilDate,
    location,
    adultsCount,
    children,
  };
}

export function* fetchLocation(
  parsedQueryString: IPremierCollectionAvailabilityParsedQuery
) {
  if (!parsedQueryString.locationName)
    return { correspondingLocation: undefined };

  const { categories: v1Categories }: { categories: ICategorizedResponse[] } =
    yield fetchPCLocationAutocomplete(parsedQueryString.locationName);

  const v1Locations: IResult[] = v1Categories.flatMap(
    (category) => category.results
  );

  const { result: correspondingLocation, matchType } = findMatchingLocation(
    v1Locations,
    parsedQueryString.locationName
  );

  if (matchType === "fuzzy" && correspondingLocation) {
    trackEvent({
      eventName: "premier_collection_location_fuzzy_fallback",
      properties: {
        search_term: parsedQueryString.locationName,
        resolved_label: correspondingLocation.label,
      },
    });
  }

  return { correspondingLocation };
}
